import { STORE_ORIGIN } from '../config';
import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { FiCheckCircle, FiXCircle, FiShoppingBag, FiMail, FiPhone, FiMapPin, FiClock } from 'react-icons/fi';
import { useCart } from '../context/CartContext';

const API_BASE_URL = `${STORE_ORIGIN}/backend/`;

const OrderConfirmation = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { clearCart } = useCart();
  
  const [orderDetails, setOrderDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchOrderDetails = async () => {
      try {
        // Get order number from URL params or localStorage
        const params = new URLSearchParams(location.search);
        let orderNumber = params.get('order');
        
        // If no order in URL, check localStorage
        if (!orderNumber) {
          const paymentResult = localStorage.getItem('paymentResult');
          if (paymentResult) {
            const result = JSON.parse(paymentResult);
            orderNumber = result.orderNumber;
            localStorage.removeItem('paymentResult');
          }
        }
        
        if (!orderNumber) {
          setError('No order information found');
          setLoading(false);
          return;
        }
        
        // Fetch order details from backend
        const response = await fetch(`${API_BASE_URL}get_order.php?order_number=${encodeURIComponent(orderNumber)}`, { headers: { Authorization: `Bearer ${sessionStorage.getItem('order_token_' + orderNumber) || ''}` } });
        const data = await response.json();
        
        if (data.success) {
          setOrderDetails(data.order);
          
          // If payment was successful and this is the first time seeing it, clear cart
          if (data.order.payment_status === 'paid' || data.order.payment_status === 'success') {
            clearCart();
          }
        } else {
          setError(data.message || 'Order not found');
        }
      } catch (err) {
        console.error('Error fetching order:', err);
        setError('Failed to load order details');
      } finally {
        setLoading(false);
      }
    };
    
    fetchOrderDetails();
  }, [location.search, clearCart]);

  const downloadInvoice = async (event) => {
    event.preventDefault();
    try {
      const response = await fetch(API_BASE_URL + 'invoice.php?order=' + encodeURIComponent(orderDetails.order_number), { headers: { Authorization: 'Bearer ' + (sessionStorage.getItem('order_token_' + orderDetails.order_number) || '') } });
      const result = await response.json();
      if (!result.success) throw new Error(result.message);
      const url = URL.createObjectURL(new Blob([result.html], { type: 'text/html;charset=utf-8' }));
      const link = document.createElement('a'); link.href = url; link.download = result.filename; link.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch { setError('Unable to download your invoice. Please try again.'); }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-green-500 mb-4"></div>
          <p className="text-gray-600">Loading order details...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white p-8 rounded-lg shadow-md max-w-md w-full text-center">
          <FiXCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-800 mb-2">Error</h1>
          <p className="text-gray-600 mb-6">{error}</p>
          <button 
            onClick={() => navigate('/')}
            className="bg-green-600 hover:bg-green-700 text-white font-medium py-2 px-6 rounded-lg transition duration-300"
          >
            Return to Home
          </button>
        </div>
      </div>
    );
  }

  const isSuccess = orderDetails?.payment_status === 'paid' || orderDetails?.payment_status === 'success';
  const isFailed = ['failed', 'expired', 'cancelled'].includes(orderDetails?.payment_status);
  const isCod = orderDetails?.payment_method === 'cod';
  const needsReview = orderDetails?.order_status === 'payment_review';

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-3xl mx-auto">
        {/* Success/Failure Header */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6 text-center">
          <div className={`inline-flex items-center justify-center w-20 h-20 rounded-full ${isSuccess ? 'bg-green-100' : 'bg-yellow-100'} mb-4`}>
            {isSuccess ? (
              <FiCheckCircle className="w-10 h-10 text-green-600" />
            ) : (
              <FiClock className="w-10 h-10 text-yellow-600" />
            )}
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            {needsReview ? 'Payment Received' : isSuccess ? 'Payment Successful!' : isCod ? 'Order Confirmed' : isFailed ? 'Payment Not Completed' : 'Payment Processing'}
          </h1>
          <p className="text-gray-600 mb-4">
            Order Number: <span className="font-semibold">{orderDetails.order_number}</span>
          </p>
          <p className={`text-lg ${isSuccess ? 'text-green-600' : 'text-yellow-600'} font-medium`}>
            {needsReview ? 'Your payment arrived after the reservation expired. We will contact you about fulfillment or a refund.' : isSuccess ? 'Your payment has been confirmed!' : isCod ? 'Your order is confirmed. Please pay on delivery.' : isFailed ? 'Your payment was not completed. You can return to your cart and try again.' : 'Your payment is being processed...'}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Order Summary */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center">
              <FiShoppingBag className="mr-2" /> Order Summary
            </h2>
            
            {orderDetails.items && orderDetails.items.length > 0 ? (
              <div className="space-y-4">
                {orderDetails.items.map((item, index) => (
                  <div key={index} className="flex items-center justify-between pb-4 border-b">
                    <div className="flex items-center space-x-4">
                      {item.image_url && (
                        <img 
                          src={item.image_url} 
                          alt={item.product_name}
                          className="w-16 h-16 object-cover rounded"
                        />
                      )}
                      <div>
                        <p className="font-medium text-gray-700">{item.product_name}</p>
                        {item.size && (
                          <p className="text-sm text-gray-500">Size: {item.size}</p>
                        )}
                        <p className="text-sm text-gray-500">Qty: {item.quantity}</p>
                      </div>
                    </div>
                    <p className="font-medium">QAR {(item.product_price * item.quantity).toFixed(2)}</p>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500">Loading items...</p>
            )}
            
            <div className="mt-6 pt-4 border-t space-y-2">
              <div className="flex justify-between">
                <span className="text-gray-600">Subtotal:</span>
                <span className="font-medium">QAR {orderDetails.subtotal || '0.00'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Shipping:</span>
                <span className="font-medium">QAR {orderDetails.shipping_cost || '0.00'}</span>
              </div>
              <div className="flex justify-between text-lg font-bold text-gray-900 pt-2 border-t">
                <span>Total:</span>
                <span>QAR {orderDetails.total || '0.00'}</span>
              </div>
            </div>
          </div>

          {/* Customer Information */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-xl font-semibold text-gray-800 mb-4">Customer Details</h2>
            
            <div className="space-y-4">
              <div className="flex items-start">
                <FiMail className="text-gray-400 mt-1 mr-3" />
                <div>
                  <p className="text-sm text-gray-500">Email</p>
                  <p className="font-medium">{orderDetails.email}</p>
                </div>
              </div>
              
              <div className="flex items-start">
                <FiPhone className="text-gray-400 mt-1 mr-3" />
                <div>
                  <p className="text-sm text-gray-500">Phone</p>
                  <p className="font-medium">{orderDetails.phone}</p>
                </div>
              </div>
              
              {orderDetails.address && (
                <div className="flex items-start">
                  <FiMapPin className="text-gray-400 mt-1 mr-3" />
                  <div>
                    <p className="text-sm text-gray-500">Delivery Address</p>
                    <p className="font-medium">{orderDetails.address}</p>
                    {orderDetails.city && (
                      <p className="text-gray-600">{orderDetails.city}</p>
                    )}
                  </div>
                </div>
              )}
              
              <div className="flex items-start">
                <FiClock className="text-gray-400 mt-1 mr-3" />
                <div>
                  <p className="text-sm text-gray-500">Order Date</p>
                  <p className="font-medium">
                    {new Date(orderDetails.created_at).toLocaleDateString('en-US', {
                      weekday: 'long',
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </p>
                </div>
              </div>
              
              <div className="pt-4 border-t">
                <p className="text-sm text-gray-500 mb-1">Payment Status</p>
                <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                  orderDetails.payment_status === 'paid' 
                    ? 'bg-green-100 text-green-800'
                    : orderDetails.payment_status === 'pending'
                    ? 'bg-yellow-100 text-yellow-800'
                    : 'bg-red-100 text-red-800'
                }`}>
                  {orderDetails.payment_status?.toUpperCase()}
                </span>
                
                {orderDetails.payment_method && (
                  <>
                    <p className="text-sm text-gray-500 mt-3 mb-1">Payment Method</p>
                    <p className="font-medium">
                      {orderDetails.payment_method === 'sadad' ? 'SADAD Online Payment' : 'Cash on Delivery'}
                    </p>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center">
          <button
            onClick={() => navigate('/')}
            className="bg-green-600 hover:bg-green-700 text-white font-medium py-3 px-8 rounded-lg transition duration-300"
          >
            Continue Shopping
          </button>
          
          <button
            onClick={() => window.print()}
            className="bg-gray-200 hover:bg-gray-300 text-gray-800 font-medium py-3 px-8 rounded-lg transition duration-300"
          >
            Print Receipt
          </button>
          
          {orderDetails.order_number && (
            <a
              href="#invoice"
              onClick={downloadInvoice}
              target="_blank"
              rel="noopener noreferrer"
              className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-3 px-8 rounded-lg transition duration-300 text-center"
            >
              Download Invoice
            </a>
          )}
        </div>
      </div>
    </div>
  );
};

export default OrderConfirmation;